/**
 * useReservationDrag — omple els ganxos `move-row` i `resize` de `useGridPointer` (runbook 7, F3).
 *
 * - **Moure de fila** (decisions 4 i 8): el fantasma segueix el punter només en vertical; la fila de
 *   destí la decideix el punter (`resolveRowTarget`) i es pinta verda o vermella (`findCollision`);
 *   auto-scroll vertical a prop de les vores; `Esc` o deixar anar fora de la graella cancel·la.
 * - **Allargar o escurçar**: la vora dreta es mou dia a dia (`resolveResizeTarget`, amb la frontera
 *   a mig dia: `checkOutOffsetX`), vermella si trepitja la següent.
 * - Res s'escriu en deixar anar: si el destí és vàlid, queda un `pendingChange` per a
 *   `StayChangeConfirmDialog`. En vermell, toast «H302 està ocupada aquells dies» i tot torna enrere.
 *
 * Per no repintar la graella a cada píxel, el desplaçament del fantasma s'escriu directament al
 * seu `style` (`ghostRef`); l'estat de React només canvia quan canvia la fila, la sortida o el xoc.
 */

import { useCallback, useEffect, useMemo, useRef, useState, type RefObject } from 'react';
import { toast } from 'sonner';
import type { RoomInfo } from '../CalendarRoomCell';
import type { ReservationCellData } from '../ReservationCell';
import type { StayChange } from '../StayChangeConfirmDialog';
import type { GestureContext, GestureHooks } from './useGridPointer';
import {
  findCollision,
  isCrossGroupMove,
  resolveResizeTarget,
  resolveRowTarget,
  resolveRowTargetInLayout,
  toDay,
  type RowBox,
} from './resolve-targets';

// ============================================
// TYPES
// ============================================

export type ReservationDragState =
  | {
      kind: 'move';
      reservationId: string;
      fromUnitId: string;
      /** `null` = el punter és fora de les files. */
      targetUnitId: string | null;
      collision: boolean;
      /** El punter és sobre una fila d'una altra propietat (mode «Totes»): no es pot deixar-hi. */
      crossGroup?: boolean;
      /**
       * Amb `crossGroup`, la fila d'una altra propietat que hi ha sota el punter (no és destí:
       * `targetUnitId` és `null`). La graella la marca en vermell abans de deixar anar.
       */
      blockedUnitId?: string | null;
    }
  | {
      kind: 'resize';
      reservationId: string;
      unitId: string;
      newCheckOut: string;
      collision: boolean;
    };

export interface UseReservationDragOptions {
  /** El contenidor amb scroll de la graella. */
  containerRef: RefObject<HTMLElement | null>;
  /** L'element que conté només les files d'habitació (sense la capçalera). */
  rowsRef: RefObject<HTMLElement | null>;
  rooms: readonly RoomInfo[];
  reservations: readonly ReservationCellData[];
  /** Primer dia visible, `YYYY-MM-DD`: l'origen de la columna 0. */
  gridStart: string | null;
  cellWidth: number;
  /**
   * Mode «Totes» del calendari: unitat → propietat. Amb grups, les files es mesuren al DOM (entre
   * blocs hi ha files separadores d'una altra alçada) i una fila d'un altre grup no és destí.
   */
  rowGroupOf?: ReadonlyMap<string, string>;
  /**
   * On cau la frontera de sortida dins de la columna, en px (`resolveResizeTarget`). Per defecte
   * `0`; la graella hi passa `cellWidth / 2` (sortides a mig dia, D4).
   */
  checkOutOffsetX?: number;
}

export interface UseReservationDragResult {
  moveRow: GestureHooks;
  resize: GestureHooks;
  drag: ReservationDragState | null;
  pendingChange: StayChange | null;
  clearPending: () => void;
  /** El fantasma hi posa la ref; el hook hi escriu el `translateY`. */
  ghostRef: RefObject<HTMLDivElement | null>;
  /** `CalendarGrid` hi posa el `cancel()` de `useGridPointer`: l'`Esc` el crida. */
  cancelGestureRef: RefObject<(() => void) | null>;
}

/** Atribut de l'element de dies de cada fila: l'origen horitzontal de la graella. */
export const GRID_DAYS_ATTR = 'data-grid-days';

/** Atribut de cada fila d'habitació (amb l'id de la unitat): el mode «Totes» les mesura. */
export const GRID_ROW_UNIT_ATTR = 'data-grid-row-unit';

/** Les files d'habitació, mesurades des de dalt de la primera fila (`rowsTop`). */
function measureRowBoxes(rowsEl: HTMLElement, rowsTop: number): RowBox[] {
  const boxes: RowBox[] = [];
  rowsEl.querySelectorAll<HTMLElement>(`[${GRID_ROW_UNIT_ATTR}]`).forEach((el) => {
    const id = el.getAttribute(GRID_ROW_UNIT_ATTR);
    if (!id) return;
    const r = el.getBoundingClientRect();
    boxes.push({ id, top: r.top - rowsTop, height: r.height });
  });
  return boxes;
}

const AUTO_SCROLL_EDGE_PX = 48;
const AUTO_SCROLL_STEP_PX = 14;

// ============================================
// HOOK
// ============================================

export function useReservationDrag({
  containerRef,
  rowsRef,
  rooms,
  reservations,
  gridStart,
  cellWidth,
  rowGroupOf,
  checkOutOffsetX = 0,
}: UseReservationDragOptions): UseReservationDragResult {
  const [drag, setDrag] = useState<ReservationDragState | null>(null);
  const [pendingChange, setPendingChange] = useState<StayChange | null>(null);
  const ghostRef = useRef<HTMLDivElement | null>(null);
  const cancelGestureRef = useRef<(() => void) | null>(null);
  const dragRef = useRef<ReservationDragState | null>(null);
  const lastCtxRef = useRef<GestureContext | null>(null);
  const rafRef = useRef<number | null>(null);

  const byId = useMemo(() => new Map(reservations.map((r) => [r.id, r])), [reservations]);
  const byUnit = useMemo(() => {
    const map = new Map<string, ReservationCellData[]>();
    for (const r of reservations) {
      if (!r.unitId) continue;
      const list = map.get(r.unitId) ?? [];
      list.push(r);
      map.set(r.unitId, list);
    }
    return map;
  }, [reservations]);
  const roomById = useMemo(() => new Map(rooms.map((r) => [r.id, r])), [rooms]);

  // Les dades canvien entre renders; els ganxos les llegeixen d'una ref i no es refan.
  const dataRef = useRef({ byId, byUnit, roomById, rooms, gridStart, cellWidth, rowGroupOf, checkOutOffsetX });
  dataRef.current = { byId, byUnit, roomById, rooms, gridStart, cellWidth, rowGroupOf, checkOutOffsetX };

  const update = useCallback((next: ReservationDragState | null) => {
    const prev = dragRef.current;
    dragRef.current = next;
    const same =
      prev === next ||
      (prev &&
        next &&
        prev.kind === next.kind &&
        prev.reservationId === next.reservationId &&
        prev.collision === next.collision &&
        (prev.kind === 'move' && next.kind === 'move'
          ? prev.targetUnitId === next.targetUnitId &&
            !!prev.crossGroup === !!next.crossGroup &&
            (prev.blockedUnitId ?? null) === (next.blockedUnitId ?? null)
          : prev.kind === 'resize' && next.kind === 'resize' && prev.newCheckOut === next.newCheckOut));
    if (!same) setDrag(next);
  }, []);

  const stopAutoScroll = useCallback(() => {
    if (rafRef.current != null) cancelAnimationFrame(rafRef.current);
    rafRef.current = null;
  }, []);

  const reset = useCallback(() => {
    stopAutoScroll();
    lastCtxRef.current = null;
    update(null);
  }, [stopAutoScroll, update]);

  // ---------- moure de fila ----------

  const computeMove = useCallback(
    (ctx: GestureContext): ReservationDragState | null => {
      const { byId: ids, byUnit: units, rooms: rows, roomById: roomsById, rowGroupOf: groups } =
        dataRef.current;
      const reservation = ctx.reservationId ? ids.get(ctx.reservationId) : undefined;
      const rowsEl = rowsRef.current;
      const container = containerRef.current;
      if (!reservation || !rowsEl || !container || rows.length === 0) return null;

      const rect = rowsEl.getBoundingClientRect();
      // Una sola propietat: totes les files fan el mateix. Amb grups (mode «Totes»), es mesuren.
      const pointed = groups
        ? resolveRowTargetInLayout(ctx.current.y - rect.top, measureRowBoxes(rowsEl, rect.top), roomsById)
        : resolveRowTarget(ctx.current.y - rect.top, rows, rect.height / rows.length);
      // Una fila d'una altra propietat no és destí: moure entre propietats es fa a Beds24.
      const crossGroup = !!pointed && isCrossGroupMove(reservation.unitId, pointed.id, groups);
      const target = crossGroup ? null : pointed;
      const collision = target
        ? !!findCollision(units.get(target.id) ?? [], reservation.checkIn, reservation.checkOut, reservation.id)
        : false;

      // El fantasma: només vertical, en coordenades del contingut (inclou el scroll fet pel camí).
      const offsetY = ctx.current.y - ctx.start.y + (container.scrollTop - ctx.startScroll.top);
      if (ghostRef.current) ghostRef.current.style.transform = `translateY(${offsetY}px)`;

      return {
        kind: 'move',
        reservationId: reservation.id,
        fromUnitId: reservation.unitId,
        targetUnitId: target?.id ?? null,
        collision: target ? target.id !== reservation.unitId && collision : false,
        crossGroup,
        blockedUnitId: crossGroup ? (pointed?.id ?? null) : null,
      };
    },
    [containerRef, rowsRef],
  );

  const autoScroll = useCallback(() => {
    rafRef.current = null;
    const ctx = lastCtxRef.current;
    const container = containerRef.current;
    if (!ctx || !container || dragRef.current?.kind !== 'move') return;
    const rect = container.getBoundingClientRect();
    const rowsTop = rowsRef.current?.getBoundingClientRect().top ?? rect.top;
    // Per dalt, la vora útil és sota la capçalera enganxada.
    const topEdge = Math.max(rect.top, Math.min(rowsTop, rect.top + 80));
    let step = 0;
    if (ctx.current.y < topEdge + AUTO_SCROLL_EDGE_PX) step = -AUTO_SCROLL_STEP_PX;
    else if (ctx.current.y > rect.bottom - AUTO_SCROLL_EDGE_PX) step = AUTO_SCROLL_STEP_PX;
    if (step !== 0) {
      const before = container.scrollTop;
      container.scrollTop = before + step;
      if (container.scrollTop !== before) update(computeMove(ctx));
    }
    rafRef.current = requestAnimationFrame(autoScroll);
  }, [computeMove, containerRef, rowsRef, update]);

  const moveRow = useMemo<GestureHooks>(
    () => ({
      onStart: (ctx) => {
        lastCtxRef.current = ctx;
        update(computeMove(ctx));
        stopAutoScroll();
        rafRef.current = requestAnimationFrame(autoScroll);
      },
      onMove: (ctx) => {
        lastCtxRef.current = ctx;
        update(computeMove(ctx));
      },
      onEnd: (ctx) => {
        const state = computeMove(ctx);
        const reservation = ctx.reservationId ? dataRef.current.byId.get(ctx.reservationId) : undefined;
        reset();
        if (!state || state.kind !== 'move' || !reservation) return;
        if (state.crossGroup) {
          toast.error('Per moure una reserva a una altra propietat, fes-ho a Beds24');
          return;
        }
        // Deixar anar fora de les files, o a la mateixa: no passa res.
        if (!state.targetUnitId || state.targetUnitId === state.fromUnitId) return;
        const target = dataRef.current.roomById.get(state.targetUnitId);
        const from = dataRef.current.roomById.get(state.fromUnitId);
        if (!target) return;
        if (state.collision) {
          toast.error(`${target.displayName} està ocupada aquells dies`);
          return;
        }
        setPendingChange({
          kind: 'move',
          reservationId: reservation.id,
          beds24Id: reservation.beds24Id ?? null,
          checkIn: toDay(reservation.checkIn),
          checkOut: toDay(reservation.checkOut),
          fromUnitName: from?.displayName ?? '—',
          toUnitId: target.id,
          toUnitName: target.displayName,
        });
      },
      onCancel: () => reset(),
    }),
    [autoScroll, computeMove, reset, stopAutoScroll, update],
  );

  // ---------- allargar o escurçar ----------

  const computeResize = useCallback(
    (ctx: GestureContext): ReservationDragState | null => {
      const {
        byId: ids,
        byUnit: units,
        gridStart: start,
        cellWidth: width,
        checkOutOffsetX: offsetX,
      } = dataRef.current;
      const reservation = ctx.reservationId ? ids.get(ctx.reservationId) : undefined;
      const days = rowsRef.current?.querySelector(`[${GRID_DAYS_ATTR}]`);
      if (!reservation || !days || !start) return null;
      const { checkOut } = resolveResizeTarget(
        ctx.current.x - days.getBoundingClientRect().left,
        reservation,
        width,
        start,
        offsetX,
      );
      const collision = !!findCollision(
        units.get(reservation.unitId) ?? [],
        reservation.checkIn,
        checkOut,
        reservation.id,
      );
      return {
        kind: 'resize',
        reservationId: reservation.id,
        unitId: reservation.unitId,
        newCheckOut: checkOut,
        collision,
      };
    },
    [rowsRef],
  );

  const resize = useMemo<GestureHooks>(
    () => ({
      onStart: (ctx) => update(computeResize(ctx)),
      onMove: (ctx) => update(computeResize(ctx)),
      onEnd: (ctx) => {
        const state = computeResize(ctx);
        const reservation = ctx.reservationId ? dataRef.current.byId.get(ctx.reservationId) : undefined;
        reset();
        if (!state || state.kind !== 'resize' || !reservation) return;
        if (state.newCheckOut === toDay(reservation.checkOut)) return;
        if (state.collision) {
          const room = dataRef.current.roomById.get(reservation.unitId);
          toast.error(`${room?.displayName ?? "L'habitació"} està ocupada aquells dies`);
          return;
        }
        setPendingChange({
          kind: 'resize',
          reservationId: reservation.id,
          beds24Id: reservation.beds24Id ?? null,
          checkIn: toDay(reservation.checkIn),
          checkOut: toDay(reservation.checkOut),
          newCheckOut: state.newCheckOut,
          price: reservation.totalPrice ?? null,
        });
      },
      onCancel: () => reset(),
    }),
    [computeResize, reset, update],
  );

  // `Esc` cancel·la el gest en curs (useGridPointer → onCancel → reset).
  const dragging = drag !== null;
  useEffect(() => {
    if (!dragging) return;
    const onKey = (e: KeyboardEvent) => {
      if (e.key === 'Escape') cancelGestureRef.current?.();
    };
    window.addEventListener('keydown', onKey);
    return () => window.removeEventListener('keydown', onKey);
  }, [dragging]);

  useEffect(() => stopAutoScroll, [stopAutoScroll]);

  const clearPending = useCallback(() => setPendingChange(null), []);

  return { moveRow, resize, drag, pendingChange, clearPending, ghostRef, cancelGestureRef };
}
